import { decamelizeKeys } from 'humps';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import { useToast } from '~/eds/src/components/hooks/useToast';
import { HttpStatusCodeType } from '~/enums/HttpStatusCodeType';
import { usePollingRequest } from '~/hooks/usePollingRequest';
import { api } from '~/redux/api/createApi';
import { mapServerAgentExecutionMessageToAgentExecutionMessage } from '~/redux/api/methods/agent-execution/transformers';
import { AgentExecutionMessage as ServerAgentExecutionMessage } from '~/redux/api/types/sylvanus/agentExecution';
import { coerceRtkqError } from '~/redux/api/utils';
import { Nullable } from '~/types/Nullable';

import { SSE_STREAM_TIMEOUT } from '../chatbot/constants';
import { useSse } from '../chatbot/hooks/useSse';
import { parseStreamEndMessage } from '../chatbot/utils';
import type {
  AgentConfiguration,
  AgentExecution,
  AgentExecutionStreamEvent,
  AgentMessage,
  AgentType,
  Entities,
  MessageContext,
  StreamEventHandlers,
} from './types';
import {
  createStreamingMessage,
  getHandlerName,
  getStreamEndpoint,
} from './utils';

export interface UseAgenticAiOptions<
  TStreamEvent extends AgentExecutionStreamEvent = AgentExecutionStreamEvent,
> {
  /** The entity type to filter agent executions by */
  entityType: Entities;

  /** Tag to group related conversations */
  executionTag: Nullable<string>;

  /** Stream event handlers for typed stream events */
  streamEventHandlers?: StreamEventHandlers<TStreamEvent>;

  /** Enable automatic success/error toasts (default: true) */
  enableAutoToasts?: boolean;

  /** Callback invoked when a streaming error occurs */
  onStreamingError?: (error: Error) => void;

  /** When true, automatically resets the agent execution state on any streaming error */
  resetOnError?: boolean;
}

export interface UseAgenticAiReturn<
  TStreamEvent extends AgentExecutionStreamEvent = AgentExecutionStreamEvent,
> {
  /** The list of agent executions */
  agentExecutions: AgentExecution[] | null;

  /** The current agent execution ID, or null if not created */
  agentExecutionId: string | null;

  /** The full agent execution object, or null if not loaded */
  agentExecution: AgentExecution | null;

  /** Messages in the agent execution */
  messages: AgentMessage[];

  /** Whether user has accepted the legal disclaimer (not 451 error) */
  hasAcceptedLegalDisclaimer: boolean;

  // Streaming state
  /** The last streaming message (if any). Not cleared after stream end */
  streamMessage: AgentMessage | null;

  /** Whether a stream is currently active */
  isStreaming: boolean;

  /** Array of thinking event texts */
  thinkingEvents: string[];

  /** The most recent raw stream event */
  currentStreamEvent: TStreamEvent | null;

  // Loading states
  /** True if any operation is in progress */
  isLoading: boolean;

  /** True if creating an agent execution */
  isCreatingAgentExecution: boolean;

  /** True if sending a message */
  isSendingMessage: boolean;

  /** True if fetching agent execution data */
  isFetchingAgentExecution: boolean;

  /** True if fetching agent executions list */
  isFetchingAgentExecutions: boolean;

  /** True when workflow routing initial suggestions polling exhausted retries without receiving suggestions */
  hasFailedToLoadInitialSuggestions: boolean;

  // Actions
  /** Create a new agent execution */
  createAgentExecution: (
    agentType: AgentType,
    agentConfiguration: AgentConfiguration,
  ) => Promise<AgentExecution | null>;

  /** Send a message to the agent execution */
  sendMessage: (
    params: {
      message: string;
      context: MessageContext;
      agentExecutionId: string;
      metadata?: Record<string, any>;
    },
    options?: {
      /** Flag to open SSE stream automatically after sending the message */
      openStream: boolean;
    },
  ) => Promise<AgentMessage | null>;

  /** Interrupt the current AI message generation */
  interruptAgentExecution: () => Promise<void>;

  /** Refetch the agent execution data */
  refetchAgentExecution: () => Promise<void>;

  /** Reset the hook state (clears agent execution) */
  resetAgentExecution: () => void;

  /** Manually close the SSE stream and interrupt AI generation */
  closeStream: () => void;

  /** Manually open the SSE stream for a given execution ID */
  openStream: (executionId?: string) => void;

  /** Refetch agent executions list (useful after accepting disclaimer) */
  refetchAgentExecutions: () => void;
}

/**
 * Core hook for managing agentic AI conversations
 */
export const useAgenticAi = <
  TStreamEvent extends AgentExecutionStreamEvent = AgentExecutionStreamEvent,
>(
  options: UseAgenticAiOptions<TStreamEvent>,
): UseAgenticAiReturn<TStreamEvent> => {
  const {
    entityType,
    executionTag,
    streamEventHandlers,
    enableAutoToasts = true,
    onStreamingError,
    resetOnError = false,
  } = options;
  const intl = useIntl();
  const { toast } = useToast();

  // Local state
  const [agentExecutionId, setAgentExecutionId] = useState<string | null>(null);
  const agentExecutionIdRef = useRef<string | null>(null);

  // Streaming state
  const [streamMessage, setStreamMessage] = useState<AgentMessage | null>(null);
  const [isStreaming, setIsStreaming] = useState(false);
  const [thinkingEvents, setThinkingEvents] = useState<string[]>([]);
  const [currentStreamEvent, setCurrentStreamEvent] =
    useState<TStreamEvent | null>(null);

  // Polling state for initial suggestions (workflow routing ONLY - temporary solution)
  const [shouldPollForInitialSuggestions, setShouldPollForInitialSuggestions] =
    useState(false);
  const [hasReceivedInitialSuggestions, setHasReceivedInitialSuggestions] =
    useState(false);

  // Only enable polling for workflow routing
  const isWorkflowRouting = executionTag === 'workflow_routing';

  // RTK Query hooks
  const [createConversationMutation, createConversationResult] =
    api.endpoints.createAgentExecution.useMutation();

  const [sendMessageMutation, sendMessageResult] =
    api.endpoints.sendAgentExecutionMessage.useMutation();

  const [interruptConversationMutation] =
    api.endpoints.interruptAgentExecution.useMutation();

  const {
    data: agentExecutions = null,
    isLoading: isFetchingAgentExecutions,
    error: errorAgentExecutions,
    refetch: refetchAgentExecutions,
  } = api.endpoints.getAgentExecutions.useQuery(
    { entityType, executionTag },
    { skip: !entityType || !executionTag },
  );

  const hasAcceptedLegalDisclaimer =
    coerceRtkqError(errorAgentExecutions)?.request?.status !==
    HttpStatusCodeType.UnavailableForLegalReasons;

  const [acceptLegalDisclaimer] =
    api.endpoints.acceptLegalDisclaimer.useLazyQuery();
  const hasAutoAccepted = useRef(false);

  useEffect(() => {
    if (!hasAcceptedLegalDisclaimer && !hasAutoAccepted.current) {
      hasAutoAccepted.current = true;
      acceptLegalDisclaimer({})
        .unwrap()
        .then(() => {
          refetchAgentExecutions();
        })
        .catch(() => {
          hasAutoAccepted.current = false;
        });
    }
  }, [
    hasAcceptedLegalDisclaimer,
    acceptLegalDisclaimer,
    refetchAgentExecutions,
  ]);

  const sseHook = useSse(intl!);

  // Use polling request for initial suggestions (workflow routing ONLY)
  const pollingResult = usePollingRequest(
    api.endpoints.getAgentExecution.useQuery,
    { agentExecutionId: agentExecutionId! },
    {
      maxRetries: 4,
      pollingInterval: 5000,
      skip:
        !agentExecutionId ||
        !isWorkflowRouting ||
        !shouldPollForInitialSuggestions ||
        hasReceivedInitialSuggestions,
    },
  );

  const {
    data: polledExecution,
    stopPolling,
    reset: resetPolling,
    isPollingFinished,
    isPollingFinishedByMaxRetries,
  } = pollingResult;
  const isPollingExecution = (pollingResult as any).isLoading || false;

  // Regular query for non-polling cases (all other agents)
  const {
    data: regularExecution,
    isLoading: isFetchingRegularExecution,
    refetch: refetchAgentExecutionQuery,
  } = api.endpoints.getAgentExecution.useQuery(
    { agentExecutionId: agentExecutionId! },
    {
      skip:
        !agentExecutionId ||
        (isWorkflowRouting && shouldPollForInitialSuggestions),
    },
  );

  // Use polled execution for workflow routing when polling, otherwise use regular execution
  const agentExecution =
    isWorkflowRouting && shouldPollForInitialSuggestions
      ? polledExecution
      : regularExecution;

  const isFetchingAgentExecution =
    isPollingExecution || isFetchingRegularExecution;

  const [messages, setMessages] = useState<AgentMessage[]>([]);

  useEffect(() => {
    setMessages(agentExecution?.messages ?? []);
  }, [agentExecution]);

  const hasInitialSuggestions = useMemo(() => {
    if (!agentExecution?.messages || agentExecution.messages.length === 0) {
      return false;
    }
    const firstMessage = agentExecution.messages[0];
    return (
      firstMessage.responseSuggestions &&
      firstMessage.responseSuggestions.length > 0
    );
  }, [agentExecution]);

  const hasFailedToLoadInitialSuggestions =
    isWorkflowRouting &&
    isPollingFinishedByMaxRetries &&
    !hasInitialSuggestions;

  useEffect(() => {
    if (
      shouldPollForInitialSuggestions &&
      (hasInitialSuggestions || isStreaming)
    ) {
      stopPolling();
      setHasReceivedInitialSuggestions(true);
      setShouldPollForInitialSuggestions(false);
    }
  }, [
    shouldPollForInitialSuggestions,
    hasInitialSuggestions,
    isStreaming,
    stopPolling,
  ]);

  useEffect(() => {
    if (shouldPollForInitialSuggestions && isPollingFinished) {
      setShouldPollForInitialSuggestions(false);
      setHasReceivedInitialSuggestions(true);
    }
  }, [shouldPollForInitialSuggestions, isPollingFinished]);

  /**
   * Reset the hook state
   */
  const resetAgentExecution = useCallback(() => {
    agentExecutionIdRef.current = null;
    setAgentExecutionId(null);
    setStreamMessage(null);
    setIsStreaming(false);
    setThinkingEvents([]);
    setCurrentStreamEvent(null);
    setShouldPollForInitialSuggestions(false);
    setHasReceivedInitialSuggestions(false);
    resetPolling();
  }, [resetPolling]);

  // Derived states
  const isCreatingAgentExecution = createConversationResult.isLoading;
  const isSendingMessage = sendMessageResult.isLoading;
  const isLoading =
    isCreatingAgentExecution ||
    isSendingMessage ||
    isFetchingAgentExecution ||
    isFetchingAgentExecutions;

  const handleStreamEvent = useCallback(
    (event: TStreamEvent) => {
      setCurrentStreamEvent(event);

      streamEventHandlers?.onStreamEvent?.(event);

      const baseEvent = event as AgentExecutionStreamEvent;

      const handlerName = getHandlerName(
        baseEvent.eventType,
      ) as keyof StreamEventHandlers<TStreamEvent>;
      const handler = streamEventHandlers?.[handlerName];

      if (handler && typeof handler === 'function') {
        handler(event);
      }

      switch (baseEvent.eventType) {
        case 'thinking':
          if (baseEvent.text) {
            setThinkingEvents((prev) => [...prev, baseEvent.text!]);
          }
          break;

        case 'message_start':
          if (agentExecutionIdRef.current) {
            setStreamMessage(
              createStreamingMessage(agentExecutionIdRef.current),
            );
          }
          break;

        case 'message_delta':
          if (baseEvent.text) {
            setStreamMessage((prev) => {
              if (!prev) return prev;
              return {
                ...prev,
                text: prev.text + baseEvent.text,
              };
            });
          }
          break;

        case 'message':
          if (baseEvent.message) {
            const transformedMsg =
              mapServerAgentExecutionMessageToAgentExecutionMessage(
                decamelizeKeys(
                  baseEvent.message,
                ) as ServerAgentExecutionMessage,
              );
            setMessages((prev) => [...prev, transformedMsg]);
          }
          break;

        case 'message_end':
          setThinkingEvents([]);
          if (baseEvent.message) {
            /**
             * upstream camelizes the entire baseEvent object, including `message`. To avoid
             * creating a new parse function, we are decamelizing only the message to be able to call
             * the API layer `mapServerAgentExecutionMessageToAgentExecutionMessage`
             **/
            const transformedMessage =
              mapServerAgentExecutionMessageToAgentExecutionMessage(
                decamelizeKeys(
                  baseEvent.message,
                ) as ServerAgentExecutionMessage,
              );
            setStreamMessage(transformedMessage);
          }
          refetchAgentExecutionQuery();
          break;

        case 'stream_end':
          setIsStreaming(false);
          setThinkingEvents([]);
          if (baseEvent.text) {
            if (enableAutoToasts) {
              toast({
                status: 'danger',
                message: parseStreamEndMessage(baseEvent.text),
              });
            }
            if (resetOnError) {
              resetAgentExecution();
            }
          }
          break;
      }
    },
    [
      streamEventHandlers,
      refetchAgentExecutionQuery,
      resetOnError,
      resetAgentExecution,
      enableAutoToasts,
      toast,
    ],
  );

  /**
   * Reset streaming state (does not clear streamMessage to preserve last received content)
   */
  const resetStream = () => {
    setIsStreaming(false);
  };

  /**
   * Open SSE stream (private - automatically called by sendMessage)
   */
  const openStream = useCallback(
    (executionId?: string) => {
      const targetExecutionId = executionId ?? agentExecutionId;

      if (!targetExecutionId) {
        return;
      }
      setIsStreaming(true);
      setThinkingEvents([]);
      setStreamMessage(null);

      sseHook.createSseConnection(
        getStreamEndpoint(targetExecutionId),
        {
          onMessage: (event: unknown) => {
            handleStreamEvent(event as TStreamEvent);
          },
          onClose: () => {
            resetStream();
          },
          onError: (err: unknown) => {
            resetStream();
            if (resetOnError) {
              resetAgentExecution();
            }
            const error = err instanceof Error ? err : new Error(String(err));
            if (enableAutoToasts) {
              toast({ status: 'danger', message: 'Streaming error occurred.' });
            }
            onStreamingError?.(error);
          },
          onAuthenticationError: (err: unknown) => {
            resetStream();
            if (resetOnError) {
              resetAgentExecution();
            }
            const error = err instanceof Error ? err : new Error(String(err));
            onStreamingError?.(error);
          },
          onRateLimitError: () => {
            resetStream();
            if (resetOnError) {
              resetAgentExecution();
            }
          },
          onTimeout: () => {
            resetStream();
            if (resetOnError) {
              resetAgentExecution();
            }
          },
          onOpen: () => {},
        },
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          openWhenHidden: true,
          timeout: SSE_STREAM_TIMEOUT,
        },
      );
    },
    [
      agentExecutionId,
      sseHook,
      handleStreamEvent,
      enableAutoToasts,
      toast,
      onStreamingError,
      resetOnError,
      resetAgentExecution,
    ],
  );

  /**
   * Create a new agent execution
   */
  const createAgentExecution = useCallback(
    async (
      agentType: AgentType,
      agentConfiguration: AgentConfiguration,
    ): Promise<AgentExecution | null> => {
      try {
        const result = await createConversationMutation({
          agentType,
          agentConfiguration,
          executionTag: executionTag ?? undefined,
        }).unwrap();

        const executionId = result?.id ?? null;
        agentExecutionIdRef.current = executionId;
        setAgentExecutionId(executionId);

        if (executionTag === 'workflow_routing') {
          setHasReceivedInitialSuggestions(false);
          setShouldPollForInitialSuggestions(true);
        }

        return result ?? null;
      } catch (err) {
        if (enableAutoToasts) {
          toast({
            status: 'danger',
            message: 'Failed to create agent execution. Please try again.',
          });
        }
        throw err;
      }
    },
    [executionTag, createConversationMutation, enableAutoToasts, toast],
  );

  /**
   * Send a message to the agent execution
   */
  const sendMessage = useCallback(
    async (
      params: {
        message: string;
        context: MessageContext;
        agentExecutionId: string;
        metadata?: Record<string, any>;
      },
      {
        openStream: shouldOpenStream = true,
      }: {
        openStream?: boolean;
      } = {},
    ): Promise<AgentMessage | null> => {
      const { message, context, agentExecutionId, metadata } = params;

      try {
        const result = await sendMessageMutation({
          agentExecutionId,
          message,
          metadata,
          context,
        }).unwrap();
        if (shouldOpenStream) openStream(agentExecutionId);

        return result ?? null;
      } catch (err) {
        if (enableAutoToasts) {
          toast({
            status: 'danger',
            message: 'Failed to send message. Please try again.',
          });
        }
        throw err;
      }
    },
    [sendMessageMutation, enableAutoToasts, openStream],
  );

  /**
   * Interrupt the current AI message generation
   */
  const interruptAgentExecution = useCallback(async (): Promise<void> => {
    if (!agentExecutionId) {
      return;
    }

    await interruptConversationMutation(agentExecutionId).unwrap();

    if (isStreaming) {
      setIsStreaming(false);
      setStreamMessage(null);
      setThinkingEvents([]);
    }
  }, [agentExecutionId, interruptConversationMutation, isStreaming]);

  /**
   * Refetch the agent execution data
   */
  const refetchAgentExecution = useCallback(async (): Promise<void> => {
    if (agentExecutionId) {
      await refetchAgentExecutionQuery();
    }
  }, [agentExecutionId, refetchAgentExecutionQuery]);

  /**
   * Close SSE stream
   */
  const closeStream = useCallback(() => {
    if (!agentExecutionId) {
      return;
    }

    resetStream();
    setThinkingEvents([]);

    // Interrupt on backend
    interruptAgentExecution();
  }, [agentExecutionId, interruptAgentExecution]);

  useEffect(() => {
    if (hasInitialSuggestions && shouldPollForInitialSuggestions) {
      setShouldPollForInitialSuggestions(false);
    }
  }, [hasInitialSuggestions, shouldPollForInitialSuggestions]);

  useEffect(() => {
    if (isStreaming && shouldPollForInitialSuggestions) {
      setShouldPollForInitialSuggestions(false);
    }
  }, [isStreaming, shouldPollForInitialSuggestions]);

  /**
   * Cleanup: interrupt active streams when agent execution changes or on unmount
   */
  useEffect(() => {
    return () => {
      if (agentExecutionId) {
        interruptConversationMutation(agentExecutionId);
      }
    };
  }, [agentExecutionId, interruptConversationMutation]);

  return {
    // State
    agentExecutions: agentExecutions?.items ?? [],
    agentExecutionId,
    agentExecution: agentExecution ?? null,
    messages,
    hasAcceptedLegalDisclaimer,

    // Streaming state
    streamMessage,
    isStreaming,
    thinkingEvents,
    currentStreamEvent,

    // Loading states
    isLoading,
    isCreatingAgentExecution,
    isSendingMessage,
    isFetchingAgentExecution,
    isFetchingAgentExecutions,
    hasFailedToLoadInitialSuggestions,

    // Actions
    createAgentExecution,
    sendMessage,
    interruptAgentExecution,
    refetchAgentExecution,
    resetAgentExecution,
    closeStream,
    openStream,
    refetchAgentExecutions,
  };
};
